/** @format */

import React, {ChangeEvent, PropsWithChildren, useMemo, useState} from 'react'

import ActionInput from '@src/components/tailwind/ActionInput'
import LoaderButton from '@src/components/tailwind/LoaderButton'
import Switch from '@src/components/tailwind/Switch'

import {useLoadingContext} from '@src/contexts'
import * as toast from '@src/toast'

import {FieldTypeKey} from '../FieldType'
import {useMimeTypeOptions} from '../useMimeTypeOptions'
import {useValidationApi} from '../ValidationApi'
import {Api} from '../ValidationRepository'

type FieldTypeSettingsProps<T extends FieldTypeKey = FieldTypeKey> = {
  type: T
  onSuccess: (fieldName: string) => void
}

export function FieldTypeSettings<T extends FieldTypeKey>(
  props: FieldTypeSettingsProps<T>,
) {
  const {type} = props
  switch (type) {
    case 'text':
      return <Text {...props} type={type} />
    case 'numeric':
      return <Numeric {...props} type={type} />
    case 'email':
      return <Base {...props} payload={{type: 'email'}} />
    case 'url':
      return <Base {...props} payload={{type: 'url'}} />
    case 'datetime-local':
      return <DateTime {...props} type={type} />
    case 'file':
      return <File {...props} type={type} />
    default:
      const exhaustiveCheck: never = type
      throw new Error(`Unhandled type="${exhaustiveCheck}"`)
  }
}

function Text({type, onSuccess}: FieldTypeSettingsProps<'text'>) {
  const [maxLength, setMaxLength] = useState('')
  const payload = {
    type,
    maxlength: parseNumber(maxLength),
  }
  return (
    <Base onSuccess={onSuccess} payload={payload}>
      <ActionInput
        label="Maximum Length"
        type="number"
        value={maxLength}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setMaxLength(e.target.value)
        }
      />
    </Base>
  )
}

function Numeric({type, onSuccess}: FieldTypeSettingsProps<'numeric'>) {
  const [maxValue, setMaxValue] = useState('')
  const [minValue, setMinValue] = useState('')
  const payload = {
    type,
    max: parseNumber(maxValue),
    min: parseNumber(minValue),
  }

  return (
    <Base onSuccess={onSuccess} payload={payload}>
      <ActionInput
        label="Minimum Value"
        type="number"
        value={minValue}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setMinValue(e.target.value)
        }
      />
      <ActionInput
        label="Maximum Value"
        type="number"
        value={maxValue}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setMaxValue(e.target.value)
        }
      />
    </Base>
  )
}

function File({type, onSuccess}: FieldTypeSettingsProps<'file'>) {
  const [selectedMimeTypes, setSelectedMimeTypes] = useState<string[]>([])
  const mimeTypeOptions = useMimeTypeOptions()
  const payload = {
    type,
    accept: selectedMimeTypes,
  }
  return (
    <Base onSuccess={onSuccess} payload={payload}>
      <ActionInput
        isMulti
        label="Accept file types"
        options={mimeTypeOptions}
        value={selectedMimeTypes}
        onChange={(selection: Array<{value: string}>) =>
          setSelectedMimeTypes(selection.map(s => s.value))
        }
        description="Leaving this unset means no file type restrictions."
        closeMenuOnSelect={false}
      />
    </Base>
  )
}

function DateTime({type, onSuccess}: FieldTypeSettingsProps<'datetime-local'>) {
  const [maxDate, setMaxDate] = useState('')
  const [minDate, setMinDate] = useState('')
  const payload = {
    type,
    max: !!maxDate ? maxDate : undefined,
    min: !!minDate ? minDate : undefined,
  }

  return (
    <Base onSuccess={onSuccess} payload={payload}>
      <ActionInput
        label="Minimum Value"
        type="datetime-local"
        value={minDate}
        max={maxDate}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setMinDate(e.target.value)
        }
      />
      <ActionInput
        label="Maximum Value"
        type="datetime-local"
        value={maxDate}
        min={minDate}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setMaxDate(e.target.value)
        }
      />
    </Base>
  )
}

type BaseProps = PropsWithChildren<{
  disableSubmit?: boolean
  onSuccess: (fieldName: string) => void
  // Omit `required` because Base will add it
  payload: Omit<Api.UpdateValidationRulePayload, 'required'>
}>

function Base(props: BaseProps) {
  const {children, disableSubmit = false, onSuccess, payload} = props

  const {ready} = useLoadingContext()
  const [fieldName, setFieldName] = useState('')
  const [required, setRequired] = useState(false)

  const api = useValidationApi()
  const currentFieldNames = useMemo(
    () => new Set(api.listValidations.data.map(v => v.fieldName)),
    [api.listValidations.data],
  )
  const [isDuplicateFieldName, setIsDuplicateFieldName] = useState(false)

  async function handleAddButtonClick(): Promise<void> {
    try {
      await api.add(fieldName, {...payload, required})
      onSuccess(fieldName)
    } catch (err) {
      toast.error(err)
    } finally {
      ready()
    }
  }

  return (
    <div>
      <ActionInput
        label="Field Name"
        value={fieldName}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const v = e.target.value
          setFieldName(v)
          setIsDuplicateFieldName(currentFieldNames.has(v))
        }}
        description={
          isDuplicateFieldName && (
            <p className="text-sm text-red-500">
              Cannot add validation for{' '}
              <code className="font-semibold">{fieldName}</code> because it
              already exists.
            </p>
          )
        }
      />
      {children}
      <Switch title="Required" checked={required} onChange={setRequired} />
      <div className="flex shrink-0 justify-end gap-2">
        <LoaderButton
          onClick={handleAddButtonClick}
          disabled={disableSubmit || !fieldName.trim() || isDuplicateFieldName}
        >
          Add
        </LoaderButton>
      </div>
    </div>
  )
}

function parseNumber(s: string): number | undefined {
  return s !== '' && !Number.isNaN(+s) ? +s : undefined
}
